import * as React from "react"
import { cn } from "../../lib/utils"

const AvatarContext = React.createContext({
  imageLoadingStatus: "idle",
  setImageLoadingStatus: () => {}
})

const Avatar = React.forwardRef(({ className, ...props }, ref) => {
  const [imageLoadingStatus, setImageLoadingStatus] = React.useState("idle")

  return (
    <AvatarContext.Provider value={{ imageLoadingStatus, setImageLoadingStatus }}>
      <div
        ref={ref}
        className={cn(
          "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
          className
        )}
        {...props}
      />
    </AvatarContext.Provider>
  )
})
Avatar.displayName = "Avatar"

const AvatarImage = React.forwardRef(({ className, src, alt, ...props }, ref) => {
  const { imageLoadingStatus, setImageLoadingStatus } = React.useContext(AvatarContext)
  const [currentSrc, setCurrentSrc] = React.useState(src)

  React.useEffect(() => {
    setCurrentSrc(src)
    setImageLoadingStatus("idle")
  }, [src, setImageLoadingStatus])

  const handleLoad = React.useCallback(() => {
    setImageLoadingStatus("loaded")
  }, [setImageLoadingStatus])

  const handleError = React.useCallback(() => {
    setImageLoadingStatus("error")
  }, [setImageLoadingStatus])

  // Don't render if no src or if error occurred
  if (!currentSrc || imageLoadingStatus === "error") {
    return null
  }

  return (
    <img
      ref={ref}
      src={currentSrc}
      alt={alt}
      onLoad={handleLoad}
      onError={handleError}
      className={cn("aspect-square h-full w-full object-cover", className)}
      {...props}
    />
  )
})
AvatarImage.displayName = "AvatarImage"

const AvatarFallback = React.forwardRef(({ className, ...props }, ref) => {
  const { imageLoadingStatus } = React.useContext(AvatarContext)

  // Only show fallback if image failed to load or no image is present
  if (imageLoadingStatus === "loaded") {
    return null
  }

  return (
    <div
      ref={ref}
      className={cn(
        "flex h-full w-full items-center justify-center rounded-full bg-muted text-sm font-medium",
        className
      )}
      {...props}
    />
  )
})
AvatarFallback.displayName = "AvatarFallback"

export { Avatar, AvatarImage, AvatarFallback }
